/**
 * Kanban ⇄ Table view mode, with its SE-5508 `view_toggled` telemetry.
 *
 * `BoardViewToggle` calls `onChange` on EVERY press, including a press on the
 * already-selected segment. Reporting that as a toggle would inflate the event
 * with taps that changed nothing, so the mode is compared before anything is
 * emitted or set.
 *
 * The comparison is held in a ref rather than read from `viewMode` state
 * because state commits asynchronously: two presses landing in the same tick
 * would both read the stale value, both pass the check, and both emit. Same
 * reasoning as the re-entry guards on the add and removal paths.
 *
 * The mode is board-local and deliberately not persisted (SE-5491).
 * @module features/player-kanban/hooks/usePipelineViewMode
 */

import { useCallback, useRef, useState } from "react";

import { AnalyticsTracker } from "@/features/analytics";

import type { BoardViewMode } from "../types/types";

/** The renderer mounted before the user touches the toggle. */
const DEFAULT_VIEW_MODE: BoardViewMode = "kanban";

/** Public API of {@link usePipelineViewMode}. */
export interface UsePipelineViewModeResult {
  /** Which renderer is currently mounted. */
  readonly viewMode: BoardViewMode;
  /** Select a renderer. A no-op when it is already selected. */
  readonly onViewModeChange: (mode: BoardViewMode) => void;
}

/**
 * Own the board's view mode and report real toggles.
 * @param boardId - The board being viewed, for event attribution.
 * @returns The {@link UsePipelineViewModeResult}.
 */
export function usePipelineViewMode(
  boardId: string | null | undefined
): UsePipelineViewModeResult {
  const [viewMode, setViewMode] = useState<BoardViewMode>(DEFAULT_VIEW_MODE);
  const viewModeRef = useRef<BoardViewMode>(DEFAULT_VIEW_MODE);

  const onViewModeChange = useCallback(
    (mode: BoardViewMode) => {
      if (viewModeRef.current === mode) return;
      viewModeRef.current = mode;
      setViewMode(mode);
      // The toggle is pure local state with no network call, so there is no
      // success signal to wait for and nothing that can fail — the user action
      // itself is the event.
      void AnalyticsTracker.pipeline.logViewToggled({
        view: mode,
        board_id: boardId ?? "",
      });
    },
    [boardId]
  );

  return { viewMode, onViewModeChange };
}
